import {
  DEFAULT_COMPANY_ADMIN_ID,
  DEFAULT_ROLES,
  DEFAULT_TEAM_MANAGER_ID,
  DEFAULT_USER_ID,
  isDefaultRoleId,
  isEmpty,
  isNotEmpty,
  isPermissionEntityScope,
  isRoleId,
  isTeamId,
  isUserId,
  SCOPE_LEVELS,
  type PermissionEntityId,
  type PermissionEntityScope,
  type DefaultRole,
  type PermissionListItem,
  type PermissionOverride,
  type PermissionType,
  type RoleId,
  type RoleType,
} from '@terros/common'
import {
  type BuildPermGraphInput,
  type PermGraphRoleConnection,
  type PermGraph,
  type PermGraphEdge,
  type PermGraphEdgeRelation,
  type PermGraphNode,
  type PermGraphNodeKind,
} from './types'

export const toPermGraphNodeKind = (entityId: PermissionEntityId): PermGraphNodeKind => {
  if (isDefaultRoleId(entityId)) return 'defaultRole'
  if (isRoleId(entityId)) return 'role'
  if (isTeamId(entityId)) return 'team'
  if (isUserId(entityId)) return 'user'
  return 'company'
}

export function buildPermGraph(input: BuildPermGraphInput): PermGraph {
  const nodes = new Map<PermissionEntityId, PermGraphNode>()
  const edges = new Map<string, PermGraphEdge>()
  const roleMap = new Map(input.roles.map((role) => [role.roleId, role]))
  const teamMap = new Map(input.teams.map((team) => [team.teamId, team]))

  const addNode = (entityId: PermissionEntityId, kind: PermGraphNodeKind, label: string, roleType?: RoleType): void => {
    if (nodes.has(entityId)) return
    nodes.set(
      entityId,
      toGraphNode({
        entityId,
        kind,
        label,
        permissionInfo: input.permissionInfo,
        permissionType: input.permissionType,
        roleType,
        targetId: input.targetId,
      })
    )
  }

  const addEdge = (source: PermissionEntityId, target: PermissionEntityId, relation: PermGraphEdgeRelation): void => {
    if (source === target) return
    edges.set(`${source}|${target}|${relation}`, { source, target, relation })
  }

  const addDefaultRole = (roleId: RoleId): void => {
    addNode(roleId, 'defaultRole', toRoleLabel(roleId))
  }

  const targetRoleType = isRoleId(input.targetId) ? roleMap.get(input.targetId)?.roleType : undefined

  addDefaultRole(DEFAULT_USER_ID)
  addNode(input.companyId, 'company', input.companyName)
  addNode(input.targetId, toPermGraphNodeKind(input.targetId), input.targetLabel, targetRoleType)
  addEdge(DEFAULT_USER_ID, input.companyId, 'inherits')

  input.teams.forEach((team) => {
    addNode(team.teamId, 'team', team.name)
    const parentId = team.parentId && teamMap.has(team.parentId) ? team.parentId : input.companyId
    addEdge(parentId, team.teamId, 'inherits')
  })

  const hasInheritedParent = (): boolean =>
    Array.from(edges.values()).some((edge) => edge.target === input.targetId && edge.relation === 'inherits')

  const memberTeamIds = input.directTeamIds.filter((teamId) => nodes.has(teamId))
  if (isRoleId(input.targetId)) {
    addRoleParent({ addDefaultRole, addEdge, roleId: input.targetId, roleType: targetRoleType })
  } else if (isNotEmpty(memberTeamIds)) {
    memberTeamIds.forEach((teamId) => addEdge(teamId, input.targetId, 'teamMembership'))
  } else if (!hasInheritedParent()) {
    addEdge(input.companyId, input.targetId, 'inherits')
  }

  const directRoleIds = new Set(input.directRoles.map((role) => role.roleId))
  const roleConnections: PermGraphRoleConnection[] = input.memberships.flatMap((membership) =>
    membership.roleIds.map((roleId) => ({ roleId, teamId: membership.teamId }))
  )
  roleConnections.push(...input.directRoles.map((role) => ({ roleId: role.roleId })))

  roleConnections.forEach(({ roleId, teamId }) => {
    const role = roleMap.get(roleId) ?? input.directRoles.find((item) => item.roleId === roleId)
    addNode(roleId, isDefaultRoleId(roleId) ? 'defaultRole' : 'role', toRoleLabel(roleId, role?.name), role?.roleType)
    addRoleParent({ addDefaultRole, addEdge, roleId, roleType: role?.roleType })
    if (teamId && nodes.has(teamId)) addEdge(roleId, teamId, 'teamRole')
    if (directRoleIds.has(roleId)) addEdge(roleId, input.targetId, 'directRole')
  })

  toPermissionSourceIds(input.permissionInfo).forEach((entityId) => {
    if (nodes.has(entityId)) return
    if (isRoleId(entityId)) {
      const role = roleMap.get(entityId)
      addNode(
        entityId,
        isDefaultRoleId(entityId) ? 'defaultRole' : 'role',
        toRoleLabel(entityId, role?.name),
        role?.roleType
      )
      addRoleParent({ addDefaultRole, addEdge, roleId: entityId, roleType: role?.roleType })
      addEdge(entityId, input.targetId, 'roleAssignment')
      return
    }
    addNode(entityId, toPermGraphNodeKind(entityId), entityId)
    addEdge(input.companyId, entityId, 'inherits')
    addEdge(entityId, input.targetId, 'inherits')
  })

  return addFlowScopes({ nodes: Array.from(nodes.values()), edges: Array.from(edges.values()) }, input.permissionInfo)
}

export function toRoleLabel(roleId: PermissionEntityId, roleName?: string): string {
  const builtIn = DEFAULT_ROLES.find((role) => role.roleId === roleId)?.name
  return builtIn ?? roleName ?? roleId
}

const toPermissionSourceIds = (info: PermissionListItem): PermissionEntityId[] => [
  ...info.allScopes.map((scope) => scope.source),
  ...(info.allOverrides?.map((override) => override.entityId) ?? []),
]

function addFlowScopes(graph: PermGraph, permissionInfo: PermissionListItem): PermGraph {
  const incoming = new Map<PermissionEntityId, PermissionEntityId[]>()
  graph.edges.forEach((edge) => incoming.set(edge.target, [...(incoming.get(edge.target) ?? []), edge.source]))

  const scopes = new Map<PermissionEntityId, PermissionEntityScope>()
  getPermGraphLevels(graph)
    .flat()
    .forEach((node) => {
      const inheritedScope = (incoming.get(node.entityId) ?? [])
        .map((entityId) => scopes.get(entityId))
        .reduce<PermissionEntityScope | undefined>((highest, scope) => {
          if (!scope) return highest
          if (!highest || SCOPE_LEVELS[scope] > SCOPE_LEVELS[highest]) return scope
          return highest
        }, undefined)
      const flowScope =
        node.isTarget && permissionInfo.primaryScope
          ? permissionInfo.primaryScope.entity
          : (node.configuredScope ?? inheritedScope)
      if (flowScope) scopes.set(node.entityId, flowScope)
    })

  return { ...graph, nodes: graph.nodes.map((node) => ({ ...node, flowScope: scopes.get(node.entityId) })) }
}

function addRoleParent({
  addDefaultRole,
  addEdge,
  roleId,
  roleType,
}: {
  addDefaultRole: (roleId: RoleId) => void
  addEdge: (source: PermissionEntityId, target: PermissionEntityId, relation: PermGraphEdgeRelation) => void
  roleId: RoleId
  roleType: RoleType | undefined
}): void {
  if (roleId === DEFAULT_COMPANY_ADMIN_ID) {
    addDefaultRole(DEFAULT_USER_ID)
    addEdge(DEFAULT_USER_ID, roleId, 'inherits')
    return
  }
  if (isDefaultRoleId(roleId)) return
  if (roleType === 'manager') {
    addDefaultRole(DEFAULT_TEAM_MANAGER_ID)
    addEdge(DEFAULT_TEAM_MANAGER_ID, roleId, 'inherits')
  }
  if (roleType === 'admin') {
    addDefaultRole(DEFAULT_COMPANY_ADMIN_ID)
    addDefaultRole(DEFAULT_USER_ID)
    addEdge(DEFAULT_USER_ID, DEFAULT_COMPANY_ADMIN_ID, 'inherits')
    addEdge(DEFAULT_COMPANY_ADMIN_ID, roleId, 'inherits')
  }
}

function toGraphNode({
  entityId,
  kind,
  label,
  permissionInfo,
  permissionType,
  roleType,
  targetId,
}: {
  entityId: PermissionEntityId
  kind: PermGraphNodeKind
  label: string
  permissionInfo: PermissionListItem
  permissionType: PermissionType
  roleType?: RoleType
  targetId: PermissionEntityId
}): PermGraphNode {
  const defaultRole = kind === 'defaultRole' ? DEFAULT_ROLES.find((role) => role.roleId === entityId) : undefined
  const builtIn = toBuiltInOverride(defaultRole, permissionType)
  const override = permissionInfo.allOverrides?.find(
    (permission) => permission.entityId === entityId && permission.scope !== 'inherit'
  )
  const configuredValue = override?.scope ?? builtIn?.scope ?? toScopeFromSources(permissionInfo, entityId)
  const configuredScope = isPermissionEntityScope(configuredValue) ? configuredValue : undefined

  return {
    entityId,
    kind,
    label,
    configuration: override ? 'override' : builtIn ? 'builtIn' : 'inherited',
    configuredScope,
    isAppliedSource: permissionInfo.primaryScope?.source === entityId,
    isTarget: entityId === targetId,
    roleType,
  }
}

const toBuiltInOverride = (
  defaultRole: DefaultRole | undefined,
  permissionType: PermissionType
): PermissionOverride | undefined =>
  defaultRole?.permissionOverrides.reduce<PermissionOverride | undefined>(
    (latest, permission) => (permission.permissionType === permissionType ? permission : latest),
    undefined
  )

const toScopeFromSources = (
  info: PermissionListItem,
  entityId: PermissionEntityId
): PermissionEntityScope | undefined => info.allScopes.find((scope) => scope.source === entityId)?.entity

export function getPermGraphLevels(graph: PermGraph): PermGraphNode[][] {
  const nodes = new Map(graph.nodes.map((node) => [node.entityId, node]))
  const indegree = new Map(graph.nodes.map((node) => [node.entityId, 0]))
  const depths = new Map(graph.nodes.map((node) => [node.entityId, 0]))
  const outgoing = new Map<PermissionEntityId, PermissionEntityId[]>()

  graph.edges.forEach((edge) => {
    indegree.set(edge.target, (indegree.get(edge.target) ?? 0) + 1)
    outgoing.set(edge.source, [...(outgoing.get(edge.source) ?? []), edge.target])
  })

  const queue = graph.nodes.filter((node) => indegree.get(node.entityId) === 0).map((node) => node.entityId)
  let visited = 0
  while (isNotEmpty(queue)) {
    const entityId = queue.shift()
    if (!entityId) continue
    visited += 1
    const depth = depths.get(entityId) ?? 0
    ;(outgoing.get(entityId) ?? []).forEach((target) => {
      depths.set(target, Math.max(depths.get(target) ?? 0, depth + 1))
      const nextIndegree = (indegree.get(target) ?? 1) - 1
      indegree.set(target, nextIndegree)
      if (nextIndegree === 0) queue.push(target)
    })
  }

  if (visited !== graph.nodes.length) throw new Error('Permission graph contains a cycle')

  const levels = new Map<number, PermGraphNode[]>()
  depths.forEach((depth, entityId) => {
    const node = nodes.get(entityId)
    if (node) levels.set(depth, [...(levels.get(depth) ?? []), node])
  })

  const sortedLevels = Array.from(levels.entries())
    .sort(([a], [b]) => a - b)
    .map(([, levelNodes]) => levelNodes.sort(compareNodes))

  return orderPermGraphLevelsByRelationships(sortedLevels, graph.edges)
}

export function orderPermGraphLevelsByRelationships(
  levels: PermGraphNode[][],
  edges: PermGraphEdge[]
): PermGraphNode[][] {
  const incoming = new Map<PermissionEntityId, PermissionEntityId[]>()
  const outgoing = new Map<PermissionEntityId, PermissionEntityId[]>()
  edges.forEach((edge) => {
    incoming.set(edge.target, [...(incoming.get(edge.target) ?? []), edge.source])
    outgoing.set(edge.source, [...(outgoing.get(edge.source) ?? []), edge.target])
  })

  for (let levelIndex = levels.length - 2; levelIndex >= 0; levelIndex -= 1) {
    sortLevelByNeighbors(levels, levelIndex, outgoing)
  }
  for (let levelIndex = 1; levelIndex < levels.length; levelIndex += 1) {
    sortLevelByNeighbors(levels, levelIndex, incoming)
  }

  return levels
}

function sortLevelByNeighbors(
  levels: PermGraphNode[][],
  levelIndex: number,
  neighborMap: Map<PermissionEntityId, PermissionEntityId[]>
): void {
  const level = levels[levelIndex]
  if (!level || level.length < 2) return

  const positions = new Map<PermissionEntityId, number>()
  levels.forEach((levelNodes) => {
    levelNodes.forEach((node, nodeIndex) => positions.set(node.entityId, (nodeIndex + 0.5) / levelNodes.length))
  })

  level.sort((a, b) => {
    const aPosition = getAverageNeighborPosition(neighborMap.get(a.entityId), positions)
    const bPosition = getAverageNeighborPosition(neighborMap.get(b.entityId), positions)
    if (aPosition !== undefined && bPosition !== undefined && aPosition !== bPosition) return aPosition - bPosition
    if (aPosition !== undefined && bPosition === undefined) return -1
    if (aPosition === undefined && bPosition !== undefined) return 1
    return compareNodes(a, b)
  })
}

function getAverageNeighborPosition(
  neighborIds: PermissionEntityId[] | undefined,
  positions: Map<PermissionEntityId, number>
): number | undefined {
  const neighborPositions = (neighborIds ?? [])
    .map((entityId) => positions.get(entityId))
    .filter((position): position is number => position !== undefined)
  if (isEmpty(neighborPositions)) return
  return neighborPositions.reduce((sum, position) => sum + position, 0) / neighborPositions.length
}

function compareNodes(a: PermGraphNode, b: PermGraphNode): number {
  const kindOrder: PermGraphNodeKind[] = ['defaultRole', 'company', 'role', 'team', 'user']
  const kindDifference = kindOrder.indexOf(a.kind) - kindOrder.indexOf(b.kind)
  if (kindDifference !== 0) return kindDifference
  return a.label.localeCompare(b.label) || a.entityId.localeCompare(b.entityId)
}
